import {
  Divider,
  ETextStyleVariant,
  GeneralMapPinOutlineIcon,
  Text,
  TextInput,
} from '@outdoorsyco/bonfire';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { IAutocompleteOption } from '@/components/switchback/Autocomplete/AutocompleteOptions';
import { ESearchFilters } from '@/constants/searchFilters';
import useMapboxQuery from '@/hooks/useMapboxQuery';
import { EFilterMethod, setFormFilter } from '@/redux/modules/searchForm';
import { getQueryParams } from '@/redux/selectors/queryParams';
import { getSearchFormDeliveryFilter } from '@/redux/selectors/search/searchForm';
import {
  EDeliveryFields,
  EDeliveryOption,
  TDeliveryFields,
} from '@/services/types/core/delivery.types';

const BringItToMeFilter: React.FC = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const form = useForm<TDeliveryFields>();

  const {
    [ESearchFilters.DELIVERY_ADDRESS]: deliveryAddressFromStore,
    [ESearchFilters.DELIVERY]: isDeliveryFromStore,
  } = useSelector(getSearchFormDeliveryFilter);
  const { [ESearchFilters.DELIVERY_ADDRESS]: deliveryAddressFromQuery } =
    useSelector(getQueryParams);

  const [addressValue, setAddressValue] = useState<string>('');
  const places = useMapboxQuery(addressValue.length < 3 ? null : { text: addressValue });

  // Track previous delivery state to detect when filter is reset
  const prevDeliveryState = useRef<{ isDelivery: boolean; hasAddress: boolean }>({
    isDelivery: !!isDeliveryFromStore,
    hasAddress: !!deliveryAddressFromStore,
  });

  // Set initial value from query params or store
  useEffect(() => {
    // Handle the case where deliveryAddressFromQuery can be string or string[]
    const addressFromQueryParam = Array.isArray(deliveryAddressFromQuery)
      ? deliveryAddressFromQuery[0]
      : deliveryAddressFromQuery;

    const initialAddress = deliveryAddressFromStore || addressFromQueryParam || '';

    if (initialAddress && initialAddress !== addressValue) {
      setAddressValue(initialAddress);
      form.setValue(EDeliveryFields.DELIVERY_ADDRESS, initialAddress);
    }
  }, [deliveryAddressFromStore, deliveryAddressFromQuery, form, addressValue]);

  // Reset local state when delivery filter is cleared from store
  useEffect(() => {
    const currentDeliveryState = {
      isDelivery: !!isDeliveryFromStore,
      hasAddress: !!deliveryAddressFromStore,
    };

    // Check if we went from having delivery/address to not having them (filter was reset)
    const wasReset =
      (prevDeliveryState.current.isDelivery || prevDeliveryState.current.hasAddress) &&
      !currentDeliveryState.isDelivery &&
      !currentDeliveryState.hasAddress;

    if (wasReset) {
      setAddressValue('');
      form.setValue(EDeliveryFields.DELIVERY_ADDRESS, '');
    }

    // Update the ref for next comparison
    prevDeliveryState.current = currentDeliveryState;
  }, [isDeliveryFromStore, deliveryAddressFromStore, form]);

  // Create address suggestions from Mapbox results
  const suggestions = places.map(p => {
    if ('name' in p) {
      // suggestion
      return {
        label: p.name_preferred || p.name,
        sublabel: p.full_address || p.place_formatted,
        value: p,
      };
    }
    // geocode
    return { label: p.place_name, value: p };
  });

  const handleAddressChange = useCallback(
    (value: string) => {
      setAddressValue(value);
      form.setValue(EDeliveryFields.DELIVERY_ADDRESS, value);

      if (value.length === 0) {
        // Clear the delivery filter when input is empty
        dispatch(
          setFormFilter({
            filters: {
              [ESearchFilters.DELIVERY]: false,
              [ESearchFilters.DELIVERY_ADDRESS]: '',
              [ESearchFilters.DELIVERY_STATIONARY]: EDeliveryOption.MOVING,
            },
            filterMethod: EFilterMethod.INPUT,
          }),
        );
      } else {
        dispatch(
          setFormFilter({
            filters: {
              [ESearchFilters.DELIVERY]: true,
              [ESearchFilters.DELIVERY_ADDRESS]: value,
              [ESearchFilters.DELIVERY_STATIONARY]: EDeliveryOption.MOVING,
            },
            filterMethod: EFilterMethod.INPUT,
          }),
        );
      }
    },
    [dispatch, form],
  );

  const handleSelectAddress = useCallback(
    (option: IAutocompleteOption) => {
      const selectedAddress = option.label;
      setAddressValue(selectedAddress);
      form.setValue(EDeliveryFields.DELIVERY_ADDRESS, selectedAddress);

      dispatch(
        setFormFilter({
          filters: {
            [ESearchFilters.DELIVERY]: true,
            [ESearchFilters.DELIVERY_ADDRESS]: selectedAddress,
            [ESearchFilters.DELIVERY_STATIONARY]: EDeliveryOption.MOVING,
          },
          filterMethod: EFilterMethod.INPUT,
        }),
      );
    },
    [dispatch, form],
  );

  return (
    <>
      <Text variant={ETextStyleVariant.MediumRegular} className="mb-6 text-gray-50">
        {intl.formatMessage({
          defaultMessage: 'Enter a location or address to browse RVs that can be brought to you.',
          id: 'PplRo0',
          description: 'Bring it to me filter modal description',
        })}
      </Text>

      <div className="mb-6">
        <div className="flex flex-col gap-4">
          <TextInput
            controlled
            label={intl.formatMessage({
              defaultMessage: 'Enter location or address',
              id: 'rG8Jtc',
            })}
            ariaLabel={intl.formatMessage({
              defaultMessage: 'Enter location or address',
              id: '0xsRJV',
              description: 'Bring it to me address input placeholder',
            })}
            inputValue={addressValue}
            onChange={handleAddressChange}
            showClearButton
          />
          {suggestions && suggestions.length > 0 && (
            <OptionsList options={suggestions} onSelectOption={handleSelectAddress} />
          )}
        </div>
      </div>
    </>
  );
};

type TOptionsListProps = {
  options: IAutocompleteOption[];
  onSelectOption: (option: IAutocompleteOption) => void;
};

const OptionsList = ({ options, onSelectOption }: TOptionsListProps) => {
  return (
    <div className="flex flex-col">
      <ul>
        {options.map((option, index) => {
          const [address, ...rest] = option.label.split(',').map(part => part.trim());

          return (
            <li key={option.label}>
              <button
                onClick={() => onSelectOption(option)}
                className="flex flex-col items-start w-full pb-4 gap-4">
                <div className="flex items-center gap-3">
                  <div className="text-2xl">
                    <GeneralMapPinOutlineIcon />
                  </div>
                  <div className="text-start">
                    <Text className="font-medium">{address}</Text>
                    {!option.sublabel && (
                      <Text className="text-start text-gray-50">{rest.join(', ')}</Text>
                    )}
                    {!!option.sublabel && (
                      <Text className="text-start text-gray-50">{option.sublabel}</Text>
                    )}
                  </div>
                </div>
                {index !== options.length - 1 && <Divider />}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default BringItToMeFilter;
